import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { MessageService } from 'primeng/api';
import {
  PersonalAccountsService,
  personalAccountApiError,
} from '../../../services/personal-accounts.service';

const PERSON_FIELDS = [
  { key: 'name', label: 'Nombre' },
  { key: 'surname', label: 'Apellido' },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Teléfono' },
  { key: 'address', label: 'Dirección' },
  { key: 'city', label: 'Ciudad' },
  { key: 'country', label: 'País ISO (2 letras)' },
  { key: 'birthdate', label: 'Nacimiento (YYYY-MM-DD)' },
  { key: 'document_type', label: 'Tipo documento' },
  { key: 'document_number', label: 'Número documento' },
] as const;

const COMPANY_FIELDS = [
  { key: 'legal_company_name', label: 'Razón social' },
  { key: 'company_number', label: 'Número de compañía' },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Teléfono' },
  { key: 'address', label: 'Dirección' },
  { key: 'city', label: 'Ciudad' },
  { key: 'country', label: 'País ISO (2 letras)' },
] as const;

@Component({
  selector: 'app-depa-personal-account-dialog',
  templateUrl: './depa-personal-account-dialog.component.html',
  styleUrl: './depa-personal-account-dialog.component.scss',
})
export class DepaPersonalAccountDialogComponent implements OnChanges {
  @Input() visible = false;
  @Input() userId: number | string | null = null;
  @Input() username = '';
  @Input() currency = 'EUR';

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() provisioned = new EventEmitter<any>();
  @Output() cancelled = new EventEmitter<void>();

  readonly documentTypeOptions = [
    { label: 'Pasaporte', value: 'PASSPORT' },
    { label: 'Documento nacional', value: 'NATIONAL_ID' },
    { label: 'Licencia de conducir', value: 'DRIVERS_LICENCE' },
    { label: 'Permiso de trabajo', value: 'WORK_PERMIT' },
  ];

  costsLoading = false;
  submitting = false;
  acceptedCosts = false;
  showIdentityForm = false;
  alias = '';
  costs: any = null;
  costsError = '';
  requestError = '';
  identityReason = '';
  verificationType: 'kyc' | 'kyb' = 'kyc';
  missingIdentityFields: string[] = [];
  softKyc: Record<string, string> = this.emptySoftKyc();
  private dismissAfterSuccess = false;

  constructor(
    private personalAccounts: PersonalAccountsService,
    private messageService: MessageService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible']?.currentValue === true || (this.visible && changes['currency'])) {
      this.resetForm();
      this.loadCosts();
    }
  }

  get currencyCode(): string {
    return String(this.currency || 'EUR').trim().toUpperCase();
  }

  get isKyb(): boolean {
    return this.verificationType === 'kyb';
  }

  get softKycFields(): ReadonlyArray<{ key: string; label: string }> {
    return this.isKyb ? COMPANY_FIELDS : PERSON_FIELDS;
  }

  get identityKindLabel(): string {
    return this.isKyb ? 'empresa (KYB)' : 'persona (KYC)';
  }

  close(): void {
    this.visible = false;
    this.visibleChange.emit(false);
    this.cancelled.emit();
  }

  onVisibleChange(value: boolean): void {
    this.visible = value;
    this.visibleChange.emit(value);
    if (!value && !this.dismissAfterSuccess) {
      this.cancelled.emit();
    }
    this.dismissAfterSuccess = false;
  }

  formatFee(fee: any): string {
    if (!fee) return 'Sin fee configurado';
    const parts: string[] = [];
    const currency = String(fee.currency || this.currencyCode).toUpperCase();
    if (fee.user_flat_fee != null && Number(fee.user_flat_fee) !== 0) {
      parts.push(`${fee.user_flat_fee} ${currency}`);
    }
    if (fee.user_fee != null && Number(fee.user_fee) !== 0) {
      const rate = Number(fee.user_fee);
      const pct = Math.abs(rate) <= 1 ? rate * 100 : rate;
      parts.push(`${pct}%`);
    }
    return parts.join(' + ') || '0';
  }

  feeTypeLabel(fee: any, fallback: string): string {
    const type = String(fee?.transaction_type || '').trim();
    return type || fallback;
  }

  submit(): void {
    const userId = Number(this.userId);
    if (!userId) {
      this.messageService.add({
        severity: 'error',
        summary: 'Cliente',
        detail: 'Selecciona un usuario antes de solicitar la cuenta.',
      });
      return;
    }
    if (!this.acceptedCosts) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Costos',
        detail: 'Debes aceptar los costos de alta y mantenimiento.',
      });
      return;
    }

    const body: any = {
      user_id: userId,
      currency: this.currencyCode,
      alias: this.alias.trim() || undefined,
      mode: 'create',
      accepted_costs: true as const,
      verification_type: this.verificationType,
    };
    const providerServiceId =
      this.costs?.priority_selection?.provider_service_id ||
      this.costs?.selected_provider_service?.id;
    const providerId =
      this.costs?.priority_selection?.provider_id ||
      this.costs?.selected_provider_service?.provider_id;
    if (providerServiceId != null) body.provider_service_id = Number(providerServiceId);
    if (providerId != null) body.provider_id = Number(providerId);

    const softKyc = this.buildSoftKycPayload();
    if (Object.keys(softKyc).length) {
      body.soft_kyc = softKyc;
    }

    this.submitting = true;
    this.requestError = '';
    this.identityReason = '';
    this.personalAccounts.create(body).subscribe({
      next: (resp) => {
        this.submitting = false;
        const account = resp?.data ?? resp;
        const status = String(resp?.provision_status || account?.status || '').trim();
        this.messageService.add({
          severity: 'success',
          summary: `Cuenta ${this.currencyCode}`,
          detail:
            resp?.message ||
            (account?.iban
              ? `Cuenta Depa lista. IBAN ${account.iban}`
              : status
                ? `Solicitud enviada (${status}).`
                : 'Solicitud enviada a Depa.'),
          life: 8000,
        });
        this.dismissAfterSuccess = true;
        this.visible = false;
        this.visibleChange.emit(false);
        this.provisioned.emit(account);
      },
      error: (err) => {
        this.submitting = false;
        this.requestError = personalAccountApiError(
          err,
          'No se pudo crear la cuenta Depa.',
        );
        const code = String(err?.code || '').toLowerCase();
        const reason = String(
          err?.reason || err?.identity_incomplete?.reason || '',
        ).trim();
        const missing = err?.identity_incomplete?.missing_fields || err?.missing_fields;
        if (Array.isArray(missing)) {
          this.missingIdentityFields = missing.map((item: any) => String(item));
        }
        if (code === 'identity_incomplete' || reason || this.missingIdentityFields.length) {
          this.showIdentityForm = true;
          this.identityReason = reason || 'Faltan datos de identidad para Depa.';
        }
        this.messageService.add({
          severity: 'error',
          summary: 'No se pudo crear la cuenta',
          detail: this.requestError,
          life: 10000,
        });
      },
    });
  }

  private loadCosts(): void {
    const userId = Number(this.userId);
    if (!userId || !this.currencyCode) {
      this.costs = null;
      this.costsError = 'Falta el usuario o la moneda.';
      return;
    }
    this.costsLoading = true;
    this.costs = null;
    this.costsError = '';
    this.personalAccounts.getCosts(this.currencyCode, userId).subscribe({
      next: (resp) => {
        this.costsLoading = false;
        this.costs = resp?.data ?? resp;
        this.applyIdentityPreview(this.costs?.identity);
        if (!this.costs) {
          this.costsError =
            'No hay fees activos de bankaccount para esta moneda.';
        }
      },
      error: (err) => {
        this.costsLoading = false;
        this.costs = null;
        this.costsError = personalAccountApiError(
          err,
          'No hay fees activos de bankaccount para esta moneda.',
        );
      },
    });
  }

  private applyIdentityPreview(identity: any): void {
    const type = String(identity?.verification_type || '').trim().toLowerCase();
    this.verificationType = type === 'kyb' ? 'kyb' : 'kyc';
    const missing = Array.isArray(identity?.missing_fields)
      ? identity.missing_fields.map((item: any) => String(item))
      : [];
    this.missingIdentityFields = missing;
    const prefill = identity?.soft_kyc && typeof identity.soft_kyc === 'object'
      ? identity.soft_kyc
      : {};
    this.softKyc = {
      ...this.emptySoftKyc(),
      ...this.pickPrefill(prefill),
    };
    this.showIdentityForm = missing.length > 0 || identity?.ready === false;
    if (missing.length) {
      this.identityReason =
        'Revisa o completa los datos de identidad que se van a enviar a Depa.';
    }
  }

  private pickPrefill(prefill: Record<string, any>): Record<string, string> {
    const out: Record<string, string> = {};
    for (const field of [...PERSON_FIELDS, ...COMPANY_FIELDS]) {
      const value = String(prefill[field.key] ?? '').trim();
      if (value) out[field.key] = value;
    }
    if (!out['legal_company_name'] && prefill['name']) {
      out['legal_company_name'] = String(prefill['name']).trim();
    }
    if (!out['name'] && prefill['legal_company_name']) {
      out['name'] = String(prefill['legal_company_name']).trim();
    }
    return out;
  }

  private resetForm(): void {
    this.acceptedCosts = false;
    this.showIdentityForm = false;
    this.alias = `${this.username || 'Cliente'} ${this.currencyCode}`.trim();
    this.costs = null;
    this.costsError = '';
    this.requestError = '';
    this.identityReason = '';
    this.verificationType = 'kyc';
    this.missingIdentityFields = [];
    this.softKyc = this.emptySoftKyc();
    this.submitting = false;
  }

  private emptySoftKyc(): Record<string, string> {
    return {
      name: '',
      surname: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      country: '',
      birthdate: '',
      document_type: 'PASSPORT',
      document_number: '',
      legal_company_name: '',
      company_number: '',
    };
  }

  private buildSoftKycPayload(): Record<string, string> {
    const out: Record<string, string> = {
      kind: this.isKyb ? 'company' : 'person',
    };
    for (const field of this.softKycFields) {
      const value = String(this.softKyc[field.key] || '').trim();
      if (value) out[field.key] = value;
    }
    if (this.isKyb && out['legal_company_name'] && !out['name']) {
      out['name'] = out['legal_company_name'];
    }
    return out;
  }
}
